/**
 * =============================================================================
 * PHS FINANCE PAGE - Unified Financial Operations
 * =============================================================================
 * Single page handling all financial views:
 * - Awaiting Invoice (jobs completed, no invoice yet)
 * - Invoice Review (invoices submitted, pending internal review)
 * - Payments (approved invoices ready for payment)
 * - Financial Dashboard (overview/metrics)
 * 
 * Route: /phs/finance
 * Query params: ?view=awaiting-invoice | invoice-review | payments | dashboard
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Clock, FileText, CreditCard, 
  CheckCircle, AlertTriangle, RefreshCw, Download,
  Search, Filter, Eye, Check, X, TrendingUp,
  BarChart3, Calendar, Building2, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PHSCommandLayout, TOKENS } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import { useFinanceUrlState } from '@/hooks/useUrlState';

const API = process.env.REACT_APP_BACKEND_URL;

// View configurations
const VIEWS = {
  'dashboard': { label: 'Financial Dashboard', icon: BarChart3 },
  'awaiting-invoice': { label: 'Awaiting Invoice', icon: Clock },
  'invoice-review': { label: 'Invoice Review', icon: FileText },
  'payments': { label: 'Payments', icon: CreditCard },
};

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '£0.00';
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP' 
  }).format(value);
};

// =============================================================================
// FINANCIAL KPI CARDS
// =============================================================================
function FinanceKPICards({ metrics, loading }) {
  const kpis = [
    { 
      label: 'Awaiting Invoice', 
      value: metrics.awaiting_invoice_count || 0,
      amount: formatCurrency(metrics.awaiting_invoice_value || 0),
      icon: Clock,
      color: '#F59E0B',
      trend: 'Jobs completed, no invoice'
    },
    { 
      label: 'Under Review', 
      value: metrics.invoice_review_count || 0,
      amount: formatCurrency(metrics.invoice_review_value || 0),
      icon: FileText,
      color: '#6366F1',
      trend: 'Invoices pending approval'
    },
    { 
      label: 'Ready for Payment', 
      value: metrics.ready_payment_count || 0,
      amount: formatCurrency(metrics.ready_payment_value || 0),
      icon: CreditCard,
      color: '#22C55E',
      trend: 'Approved, awaiting payment'
    },
    { 
      label: 'Paid This Month', 
      value: metrics.paid_count || 0,
      amount: formatCurrency(metrics.paid_value || 0),
      icon: CheckCircle,
      color: '#0D9488',
      trend: `+${metrics.paid_trend || 0}% vs last month`
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {kpis.map((kpi, idx) => {
        const Icon = kpi.icon;
        return (
          <Card key={idx} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">{kpi.label}</p>
                  <p className="text-2xl font-bold text-slate-800">{kpi.value}</p>
                  <p className="text-lg font-semibold" style={{ color: kpi.color }}>{kpi.amount}</p>
                  <p className="text-xs text-slate-400 mt-1">{kpi.trend}</p>
                </div>
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${kpi.color}15` }}
                >
                  <Icon className="w-5 h-5" style={{ color: kpi.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// =============================================================================
// AWAITING INVOICE TABLE
// =============================================================================
function AwaitingInvoiceView({ jobs, loading, onRefresh, onViewJob }) {
  const filteredJobs = useMemo(() => {
    return jobs.filter(j => 
      ['Completed', 'Awaiting Invoice'].includes(j.status) && 
      !j.invoice_id
    );
  }, [jobs]);

  return (
    <div data-testid="awaiting-invoice-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Awaiting Invoice</h2>
          <p className="text-sm text-slate-500">Jobs completed but not yet invoiced by contractor</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Job Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Site / Plot</th>
                <th className="text-left p-3 font-medium text-slate-600">Category</th>
                <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
                <th className="text-left p-3 font-medium text-slate-600">Completed</th>
                <th className="text-left p-3 font-medium text-slate-600">Quote Value</th>
                <th className="text-left p-3 font-medium text-slate-600">Days Since</th>
                <th className="text-left p-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredJobs.map(job => {
                const completedDate = job.completed_at ? new Date(job.completed_at) : null;
                const daysSince = completedDate ? Math.floor((new Date() - completedDate) / 86400000) : 0;
                
                return (
                  <tr key={job.id} className="hover:bg-slate-50">
                    <td className="p-3">
                      <span className="font-medium text-teal-600">{job.job_reference}</span>
                    </td>
                    <td className="p-3 text-slate-700">
                      {job.park_name || job.site_name} / {job.plot_number || '-'}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline">{job.category || job.job_type}</Badge>
                    </td>
                    <td className="p-3 text-slate-700">{job.contractor_name || 'Unassigned'}</td>
                    <td className="p-3 text-slate-500 text-xs">
                      {completedDate?.toLocaleDateString() || '-'}
                    </td>
                    <td className="p-3 font-medium text-slate-700">
                      {formatCurrency(job.approved_quote_amount || job.quote_amount)}
                    </td>
                    <td className="p-3">
                      <Badge variant={daysSince > 7 ? 'destructive' : daysSince > 3 ? 'secondary' : 'outline'}>
                        {daysSince} days
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onViewJob(job)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    No jobs awaiting invoice
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// INVOICE REVIEW TABLE
// =============================================================================
function InvoiceReviewView({ jobs, loading, onRefresh, onViewJob, onApproveInvoice, onQueryInvoice }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [queryNote, setQueryNote] = useState('');
  const [showQueryModal, setShowQueryModal] = useState(false);
  
  const filteredJobs = useMemo(() => {
    return jobs.filter(j => 
      ['Invoice Submitted', 'Invoice Queried'].includes(j.status)
    );
  }, [jobs]);

  const handleQuery = () => {
    if (!queryNote.trim()) {
      toast.error('Please enter a query reason');
      return;
    }
    onQueryInvoice(selectedJob, queryNote);
    setShowQueryModal(false);
    setQueryNote('');
    setSelectedJob(null);
  };

  return (
    <div data-testid="invoice-review-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Invoice Review</h2>
          <p className="text-sm text-slate-500">Invoices submitted by contractors pending approval</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Job Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
                <th className="text-left p-3 font-medium text-slate-600">Invoice Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Invoice Amount</th>
                <th className="text-left p-3 font-medium text-slate-600">Quote Amount</th>
                <th className="text-left p-3 font-medium text-slate-600">Variance</th>
                <th className="text-left p-3 font-medium text-slate-600">Status</th>
                <th className="text-left p-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredJobs.map(job => {
                const invoiceAmount = job.invoice_amount || 0;
                const quoteAmount = job.approved_quote_amount || job.quote_amount || 0;
                const variance = invoiceAmount - quoteAmount;
                const variancePercent = quoteAmount > 0 ? ((variance / quoteAmount) * 100).toFixed(1) : 0;
                
                return (
                  <tr key={job.id} className="hover:bg-slate-50">
                    <td className="p-3">
                      <span className="font-medium text-teal-600">{job.job_reference}</span>
                    </td>
                    <td className="p-3 text-slate-700">{job.contractor_name}</td>
                    <td className="p-3 text-slate-600">{job.invoice_reference || '-'}</td>
                    <td className="p-3 font-semibold text-slate-800">
                      {formatCurrency(invoiceAmount)}
                    </td>
                    <td className="p-3 text-slate-600">{formatCurrency(quoteAmount)}</td>
                    <td className="p-3">
                      <span className={variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-slate-600'}>
                        {variance > 0 ? '+' : ''}{formatCurrency(variance)} ({variancePercent}%)
                      </span>
                    </td>
                    <td className="p-3">
                      <Badge variant={job.status === 'Invoice Queried' ? 'destructive' : 'secondary'}>
                        {job.status}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onViewJob(job)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onApproveInvoice(job)}
                          className="text-green-600 hover:text-green-700"
                          title="Approve Invoice"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedJob(job);
                            setShowQueryModal(true);
                          }}
                          className="text-red-600 hover:text-red-700"
                          title="Query Invoice"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    No invoices pending review
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Query Modal */}
      <Dialog open={showQueryModal} onOpenChange={setShowQueryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Query Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-3">
              Job: <strong>{selectedJob?.job_reference}</strong> | 
              Amount: <strong>{formatCurrency(selectedJob?.invoice_amount)}</strong>
            </p>
            <Textarea
              placeholder="Enter the reason for querying this invoice..."
              value={queryNote}
              onChange={(e) => setQueryNote(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQueryModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleQuery}>Send Query</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// PAYMENTS VIEW
// =============================================================================
function PaymentsView({ jobs, loading, onRefresh, onViewJob, onMarkPaid }) {
  const filteredJobs = useMemo(() => {
    return jobs.filter(j => 
      ['Invoice Approved', 'Ready For Payment'].includes(j.status)
    );
  }, [jobs]);

  const totalValue = useMemo(() => {
    return filteredJobs.reduce((sum, j) => sum + (j.invoice_amount || 0), 0);
  }, [filteredJobs]);

  return (
    <div data-testid="payments-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Payments</h2>
          <p className="text-sm text-slate-500">
            Approved invoices ready for payment | Total: <strong>{formatCurrency(totalValue)}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Payment Run
          </Button>
        </div>
      </div>
      
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">
                  <input type="checkbox" className="rounded" />
                </th>
                <th className="text-left p-3 font-medium text-slate-600">Job Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Contractor</th>
                <th className="text-left p-3 font-medium text-slate-600">Site</th>
                <th className="text-left p-3 font-medium text-slate-600">Invoice Ref</th>
                <th className="text-left p-3 font-medium text-slate-600">Amount</th>
                <th className="text-left p-3 font-medium text-slate-600">Approved</th>
                <th className="text-left p-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredJobs.map(job => (
                <tr key={job.id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <input type="checkbox" className="rounded" />
                  </td>
                  <td className="p-3">
                    <span className="font-medium text-teal-600">{job.job_reference}</span>
                  </td>
                  <td className="p-3 text-slate-700">{job.contractor_name}</td>
                  <td className="p-3 text-slate-600">{job.park_name || job.site_name}</td>
                  <td className="p-3 text-slate-600">{job.invoice_reference || '-'}</td>
                  <td className="p-3 font-semibold text-slate-800">
                    {formatCurrency(job.invoice_amount)}
                  </td>
                  <td className="p-3 text-xs text-slate-500">
                    {job.invoice_approved_at ? new Date(job.invoice_approved_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onViewJob(job)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => onMarkPaid(job)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <CreditCard className="w-4 h-4 mr-1" />
                        Mark Paid
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400">
                    No payments pending
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// FINANCIAL DASHBOARD VIEW
// =============================================================================
function FinanceDashboardView({ metrics, jobs, loading }) {
  return (
    <div data-testid="finance-dashboard-view">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Financial Dashboard</h2>
        <p className="text-sm text-slate-500">Overview of all financial operations</p>
      </div>
      
      {/* Metrics will be populated from real aggregations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Monthly Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-center justify-center text-slate-400">
              <BarChart3 className="w-12 h-12 mr-3 opacity-30" />
              <span>Revenue chart - Real data from MongoDB aggregations</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Outstanding Invoices */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Outstanding by Contractor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Will be populated from real data */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Total Outstanding</span>
                <span className="font-semibold">{formatCurrency(metrics.total_outstanding || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Avg Days to Invoice</span>
                <span className="font-semibold">{metrics.avg_days_to_invoice || 0} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Avg Days to Payment</span>
                <span className="font-semibold">{metrics.avg_days_to_payment || 0} days</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN FINANCE PAGE
// =============================================================================
function FinancePage({ wyldecrestUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL-driven state
  const { getParam, updateParams } = useFinanceUrlState();
  const currentView = getParam('view', 'dashboard');
  const urlSearch = getParam('search', '');
  const urlStatus = getParam('status', '');
  
  const [jobs, setJobs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [jobCounts, setJobCounts] = useState({});

  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch jobs and metrics
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch jobs with financial statuses
      const jobsRes = await axios.get(`${API}/api/wyldecrest/jobs`, {
        params: { 
          limit: 500,
          include_financial: true 
        },
        headers
      });
      setJobs(jobsRes.data?.jobs || []);

      // Calculate counts
      const allJobs = jobsRes.data?.jobs || [];
      setJobCounts({
        awaiting_invoice: allJobs.filter(j => ['Completed', 'Awaiting Invoice'].includes(j.status) && !j.invoice_id).length,
        invoice_review: allJobs.filter(j => ['Invoice Submitted', 'Invoice Queried'].includes(j.status)).length,
        payments: allJobs.filter(j => ['Invoice Approved', 'Ready For Payment'].includes(j.status)).length,
        paid: allJobs.filter(j => j.status === 'Paid').length,
      });

      // Fetch financial metrics
      try {
        const metricsRes = await axios.get(`${API}/api/phs/finance/metrics`, { headers });
        setMetrics(metricsRes.data || {});
      } catch {
        // Calculate basic metrics from jobs
        setMetrics({
          awaiting_invoice_count: allJobs.filter(j => ['Completed', 'Awaiting Invoice'].includes(j.status) && !j.invoice_id).length,
          awaiting_invoice_value: allJobs.filter(j => ['Completed', 'Awaiting Invoice'].includes(j.status) && !j.invoice_id)
            .reduce((sum, j) => sum + (j.approved_quote_amount || j.quote_amount || 0), 0),
          invoice_review_count: allJobs.filter(j => ['Invoice Submitted', 'Invoice Queried'].includes(j.status)).length,
          invoice_review_value: allJobs.filter(j => ['Invoice Submitted', 'Invoice Queried'].includes(j.status))
            .reduce((sum, j) => sum + (j.invoice_amount || 0), 0),
          ready_payment_count: allJobs.filter(j => ['Invoice Approved', 'Ready For Payment'].includes(j.status)).length,
          ready_payment_value: allJobs.filter(j => ['Invoice Approved', 'Ready For Payment'].includes(j.status))
            .reduce((sum, j) => sum + (j.invoice_amount || 0), 0),
          paid_count: allJobs.filter(j => j.status === 'Paid').length,
          paid_value: allJobs.filter(j => j.status === 'Paid')
            .reduce((sum, j) => sum + (j.invoice_amount || j.approved_quote_amount || 0), 0),
        });
      }
    } catch (err) {
      console.error('Failed to fetch finance data:', err);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // URL-driven view change
  const setView = (view) => {
    updateParams({ view, search: '', status: '' });
  };

  // URL-driven search change
  const handleSearchChange = (value) => updateParams({ search: value });

  // URL-driven filtered jobs
  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    
    // Search filter
    if (urlSearch) {
      const q = urlSearch.toLowerCase();
      result = result.filter(j => 
        j.job_reference?.toLowerCase().includes(q) ||
        j.site_name?.toLowerCase().includes(q) ||
        j.contractor_name?.toLowerCase().includes(q) ||
        j.description?.toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [jobs, urlSearch]);

  // Handle job view
  const handleViewJob = (job) => {
    navigate(`/phs/helpdesk/job/${job.id}`);
  };

  // Handle invoice approval
  const handleApproveInvoice = async (job) => {
    try {
      await axios.post(`${API}/api/wyldecrest/jobs/${job.id}/approve-invoice`, {}, { headers });
      toast.success(`Invoice approved for ${job.job_reference}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  // Handle invoice query
  const handleQueryInvoice = async (job, note) => {
    try {
      await axios.post(`${API}/api/wyldecrest/jobs/${job.id}/query-invoice`, { note }, { headers });
      toast.success(`Invoice queried for ${job.job_reference}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to query invoice');
    }
  };

  // Handle mark paid
  const handleMarkPaid = async (job) => {
    try {
      await axios.post(`${API}/api/wyldecrest/jobs/${job.id}/mark-paid`, {}, { headers });
      toast.success(`Payment recorded for ${job.job_reference}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wyldecrest_token');
    localStorage.removeItem('wyldecrest_user');
    navigate('/admin/login');
  };

  return (
    <PHSCommandLayout
      user={wyldecrestUser}
      onLogout={handleLogout}
      jobCounts={jobCounts}
    >
      <div className="p-4 lg:p-6" data-testid="phs-finance-page">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-slate-800">Finance</h1>
          <p className="text-sm text-slate-500">Manage invoices, payments, and financial operations</p>
        </div>

        {/* KPI Cards */}
        <FinanceKPICards metrics={metrics} loading={loading} />

        {/* View Tabs - Standardized underline style */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto pb-px">
          {Object.entries(VIEWS).map(([key, config]) => {
            const Icon = config.icon;
            const isActive = currentView === key;
            return (
              <button
                key={key}
                onClick={() => setView(key)}
                data-testid={`finance-tab-${key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  isActive 
                    ? 'border-teal-600 text-teal-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {config.label}
                {key !== 'dashboard' && jobCounts[key.replace('-', '_')] > 0 && (
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${isActive ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'}`}
                  >
                    {jobCounts[key.replace('-', '_')]}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* View Content */}
        {currentView === 'dashboard' && (
          <FinanceDashboardView metrics={metrics} jobs={filteredJobs} loading={loading} />
        )}
        {currentView === 'awaiting-invoice' && (
          <AwaitingInvoiceView 
            jobs={filteredJobs} 
            loading={loading} 
            onRefresh={fetchData}
            onViewJob={handleViewJob}
            searchTerm={urlSearch}
            onSearchChange={handleSearchChange}
          />
        )}
        {currentView === 'invoice-review' && (
          <InvoiceReviewView 
            jobs={filteredJobs} 
            loading={loading} 
            onRefresh={fetchData}
            onViewJob={handleViewJob}
            onApproveInvoice={handleApproveInvoice}
            onQueryInvoice={handleQueryInvoice}
            searchTerm={urlSearch}
            onSearchChange={handleSearchChange}
          />
        )}
        {currentView === 'payments' && (
          <PaymentsView 
            jobs={filteredJobs} 
            loading={loading} 
            onRefresh={fetchData}
            onViewJob={handleViewJob}
            onMarkPaid={handleMarkPaid}
            searchTerm={urlSearch}
            onSearchChange={handleSearchChange}
          />
        )}
      </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(FinancePage, 'helpdesk');
